import React from "react";
import { BookOpen, CheckCircle2, ChevronRight, Layers } from "lucide-react";

import { PageRouteHeadline } from "@/components/Layout/PageRouteHeadline";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";

import {
  courseOverviewMainCardClassName,
  universityCourseBodyTextClassName,
  universityCourseFieldLabelClassName,
  universityCourseFieldStackClassName,
  universityCourseMutedTextClassName,
} from "./courseOverviewCardShell";
import { CourseOverviewProgress } from "./CourseOverviewProgress";
import { UniversityCourseLessonNavigator } from "./UniversityCourseLessonNavigator";

export type CourseOverviewModuleLesson = {
  id: number;
  title: string;
  description: string;
  imageSrc?: string;
  imageGradient?: string;
  readingTimeLabel: string;
};

export type CourseOverviewModule = {
  id: number;
  title: string;
  description: string;
  duration: string;
  lessons: CourseOverviewModuleLesson[];
};

export type CourseOverviewDetails = {
  longDescription: string;
  learningObjectives: string[];
  modules: CourseOverviewModule[];
};

export type CourseOverviewViewProps = {
  courseTitle: string;
  thumbnailSrc?: string;
  thumbnailGradient: string;
  courseDetails: CourseOverviewDetails;
  isEnrolled: boolean;
  enrolling: boolean;
  progress: number;
  hasCourseProgress: boolean;
  showFullAbout: boolean;
  enrollModalOpen: boolean;
  enrollRequiredOpen: boolean;
  onToggleAbout: () => void;
  onEnroll: () => void;
  onStartLearning: () => void;
  onContinueCourse: () => void;
  onEnrollModalClose: () => void;
  onEnrollRequiredClose: () => void;
  onEnrollFromRequired: () => void;
  onOpenLesson: (moduleId: number, lessonId: number) => void;
  isLessonCompleted: (moduleId: number, lessonId: number) => boolean;
  isModuleCompleted: (moduleId: number) => boolean;
  isModuleInProgress: (moduleId: number) => boolean;
};

export function CourseOverviewView({
  courseTitle,
  thumbnailSrc,
  thumbnailGradient,
  courseDetails,
  isEnrolled,
  enrolling,
  progress,
  hasCourseProgress,
  showFullAbout,
  enrollModalOpen,
  enrollRequiredOpen,
  onToggleAbout,
  onEnroll,
  onStartLearning,
  onContinueCourse,
  onEnrollModalClose,
  onEnrollRequiredClose,
  onEnrollFromRequired,
  onOpenLesson,
  isLessonCompleted,
  isModuleCompleted,
  isModuleInProgress,
}: CourseOverviewViewProps): React.ReactElement {
  const shadcnInset = useShadcnInsetChrome();
  const paragraphs = courseDetails.longDescription.split("\n").filter((p) => p.trim());
  const firstParagraph = paragraphs[0] || courseDetails.longDescription;
  const remainingParagraphs = paragraphs.slice(1);
  const totalLessons = courseDetails.modules.reduce(
    (total, module) => total + module.lessons.length,
    0,
  );
  const canExpandAbout =
    remainingParagraphs.length > 0 || courseDetails.learningObjectives.length > 0;

  return (
    <>
      <div data-university-course-overview-page="" className="flex w-full min-w-0 flex-col gap-4">
        {!shadcnInset ? (
          <PageRouteHeadline>{courseTitle}</PageRouteHeadline>
        ) : null}

        <div data-university-course-overview-main-card="" className={courseOverviewMainCardClassName}>
          {thumbnailSrc ? (
            <img
              src={thumbnailSrc}
              alt={courseTitle}
              width={1200}
              height={400}
              draggable={false}
              data-university-course-overview-hero=""
              className="h-[200px] w-full rounded-[6px] border border-border object-cover object-center md:h-[280px]"
            />
          ) : (
            <div
              data-university-course-overview-hero=""
              className="h-[200px] w-full rounded-[6px] border border-border bg-cover bg-center md:h-[280px]"
              style={{ backgroundImage: thumbnailGradient }}
              role="img"
              aria-label={courseTitle}
            />
          )}

          <div
            data-university-course-overview-toolbar=""
            className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div
              data-university-course-overview-stats=""
              className="flex flex-wrap items-center gap-3"
            >
              <div className="inline-flex items-center gap-2 rounded-[6px] border border-border px-3 py-1.5 text-sm">
                <Layers className="size-4 text-[color:var(--brand-tropical-surf)]" aria-hidden />
                <span
                  data-university-course-muted=""
                  className={universityCourseMutedTextClassName}
                >
                  {courseDetails.modules.length} modules
                </span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-[6px] border border-border px-3 py-1.5 text-sm">
                <BookOpen className="size-4 text-[color:var(--brand-tropical-surf)]" aria-hidden />
                <span data-university-course-muted="" className={universityCourseMutedTextClassName}>
                  {totalLessons} lessons
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              {isEnrolled ? <CourseOverviewProgress value={progress} /> : null}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant={isEnrolled ? "outline-gray" : "gradient"}
                  size="sm"
                  data-font="primary"
                  className="shrink-0"
                  disabled={isEnrolled || enrolling}
                  onClick={onEnroll}
                >
                  <span className="normal-case tracking-normal">
                    {enrolling ? "Enrolling..." : isEnrolled ? "Enrolled" : "Enroll now"}
                  </span>
                  {!isEnrolled && !enrolling ? <ChevronRight className="size-4" /> : null}
                </Button>
                {isEnrolled ? (
                  <Button
                    type="button"
                    variant="gradient"
                    size="sm"
                    data-font="primary"
                    className="shrink-0"
                    onClick={onContinueCourse}
                  >
                    <span className="normal-case tracking-normal">
                      {hasCourseProgress ? "Continue course" : "Begin course"}
                    </span>
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          <section data-university-course-overview-about="" className="flex flex-col gap-3">
            <Field className="gap-1.5">
              <FieldLabel
                className={`text-xl ${universityCourseFieldLabelClassName}`}
              >
                About the course
              </FieldLabel>
              <div className={universityCourseFieldStackClassName}>
                <p
                  data-university-course-overview-body=""
                  className={universityCourseBodyTextClassName}
                >
                  {firstParagraph}
                </p>

                {showFullAbout && remainingParagraphs.length > 0
                  ? remainingParagraphs.map((paragraph, index) => (
                      <p
                        key={index}
                        data-university-course-overview-body=""
                        className={universityCourseBodyTextClassName}
                      >
                        {paragraph}
                      </p>
                    ))
                  : null}

                {showFullAbout && courseDetails.learningObjectives.length > 0 ? (
                  <div className="flex flex-col gap-3 pt-1.5">
                    <h3
                      className={`text-base ${universityCourseFieldLabelClassName}`}
                    >
                      What you&apos;ll learn
                    </h3>
                    <div className="grid gap-3 md:grid-cols-2">
                      {courseDetails.learningObjectives.map((objective, index) => (
                        <div
                          key={index}
                          data-university-course-overview-body=""
                          className={`flex items-start gap-2 ${universityCourseBodyTextClassName}`}
                        >
                          <CheckCircle2
                            className="mt-0.5 size-4 shrink-0 text-[color:var(--brand-tropical-surf)]"
                            aria-hidden
                          />
                          <span>{objective}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {canExpandAbout ? (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    data-font="primary"
                    data-university-course-see-more=""
                    className="h-auto min-h-0 justify-start self-start p-0 text-[color:var(--brand-tropical-surf)] no-underline hover:bg-transparent hover:text-[color:var(--brand-tropical-surf)] hover:no-underline"
                    style={{ paddingInline: 0, paddingBlock: 0, height: "auto" }}
                    onClick={onToggleAbout}
                  >
                    <span className="normal-case tracking-normal">
                      {showFullAbout ? "See less" : "See more"}
                    </span>
                  </Button>
                ) : null}
              </div>
            </Field>
          </section>

          <section data-university-course-overview-content="" className="flex flex-col gap-3">
            <UniversityCourseLessonNavigator
              modules={courseDetails.modules}
              isLessonCompleted={isLessonCompleted}
              isModuleCompleted={isModuleCompleted}
              isModuleInProgress={isModuleInProgress}
              onSelectLesson={onOpenLesson}
            />
          </section>
        </div>
      </div>

      <Dialog open={enrollModalOpen} onOpenChange={(open) => !open && onEnrollModalClose()}>
        <DialogContent className="sm:max-w-md" data-university-course-enroll-dialog="">
          <DialogHeader className="items-center text-center">
            <DialogTitle className="font-[family-name:var(--font-primary)] text-2xl font-medium text-[color:var(--brand-tropical-surf)]">
              Enroll in this course?
            </DialogTitle>
            <DialogDescription
              data-university-course-overview-body=""
              className={universityCourseBodyTextClassName}
            >
              Enroll in <strong>{courseTitle}</strong> to unlock lessons and track your progress.
              You can start learning as soon as you enroll.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-center">
            <Button
              type="button"
              variant="outline-gray"
              size="sm"
              data-font="primary"
              disabled={enrolling}
              onClick={onEnrollModalClose}
            >
              <span className="normal-case tracking-normal">Not now</span>
            </Button>
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              disabled={enrolling}
              onClick={onStartLearning}
            >
              <span className="normal-case tracking-normal">
                {enrolling ? "Enrolling..." : "Enroll now"}
              </span>
              {!enrolling ? <ChevronRight className="size-4" /> : null}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={enrollRequiredOpen}
        onOpenChange={(open) => !open && onEnrollRequiredClose()}
      >
        <DialogContent className="sm:max-w-md" data-university-course-enroll-required-dialog="">
          <DialogHeader className="items-center text-center">
            <DialogTitle className="font-[family-name:var(--font-primary)] text-2xl font-medium text-[color:var(--brand-tropical-surf)]">
              Enrollment required
            </DialogTitle>
            <DialogDescription
              data-university-course-overview-body=""
              className={universityCourseBodyTextClassName}
            >
              You need to enroll in <strong>{courseTitle}</strong> before you can open lessons.
              You can still browse the modules and lesson list below.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-center">
            <Button
              type="button"
              variant="outline-gray"
              size="sm"
              data-font="primary"
              disabled={enrolling}
              onClick={onEnrollRequiredClose}
            >
              <span className="normal-case tracking-normal">Not now</span>
            </Button>
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              disabled={enrolling}
              onClick={onEnrollFromRequired}
            >
              <span className="normal-case tracking-normal">
                {enrolling ? "Enrolling..." : "Enroll now"}
              </span>
              {!enrolling ? <ChevronRight className="size-4" /> : null}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
